/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Own high-frequency activity editor selection, zoom and reference pane state.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types; shared transient reset registry
 * @forbidden API entities; permissions; tokens; routes; app bootstrap
 * @public-api activities module only
 * @side-effects in-memory UI updates
 * @tests module-contracts.test.ts
 */

import { create } from 'zustand';
import type { AssignmentDraft } from '../../../shared/lib/model';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';

interface ActivityUiState {
  selectedQuestion: number;
  zoom: number;
  referenceOpen: boolean;
  referenceTab: 'q' | 'a';
  referenceZoom: number;
  referencePage: number;
  assignment: AssignmentDraft | null;

  setSelectedQuestion(index: number): void;
  changeZoom(delta: number): void;
  setZoom(zoom: number): void;
  toggleReference(): void;
  setReferenceOpen(open: boolean): void;
  setReferenceTab(tab: 'q' | 'a'): void;
  changeReferenceZoom(delta: number): void;
  setReferenceZoom(zoom: number): void;
  changeReferencePage(delta: number): void;
  setAssignment(draft: AssignmentDraft | null): void;
  patchAssignment(patch: Partial<AssignmentDraft>): void;
  reset(): void;
}

function initial() {
  return {
    selectedQuestion: 0,
    zoom: 100,
    referenceOpen: true,
    referenceTab: 'q' as 'q' | 'a',
    referenceZoom: 100,
    referencePage: 1,
    assignment: null as AssignmentDraft | null,
  };
}

export const useActivityUiStore = create<ActivityUiState>((set) => ({
  ...initial(),

  setSelectedQuestion: (selectedQuestion) => set({ selectedQuestion }),
  changeZoom: (delta) =>
    set((state) => ({ zoom: Math.max(50, Math.min(160, state.zoom + delta)) })),
  setZoom: (zoom) => set({ zoom }),
  toggleReference: () => set((state) => ({ referenceOpen: !state.referenceOpen })),
  setReferenceOpen: (referenceOpen) => set({ referenceOpen }),
  setReferenceTab: (referenceTab) => set({ referenceTab }),
  changeReferenceZoom: (delta) =>
    set((state) => ({ referenceZoom: Math.max(50, Math.min(200, state.referenceZoom + delta)) })),
  setReferenceZoom: (referenceZoom) => set({ referenceZoom }),
  changeReferencePage: (delta) =>
    set((state) => ({ referencePage: Math.max(1, Math.min(9, state.referencePage + delta)) })),

  setAssignment: (assignment) => set({ assignment }),
  patchAssignment: (patch) =>
    set((state) => ({ assignment: state.assignment ? { ...state.assignment, ...patch } : null })),

  reset: () => set({ ...initial() }),
}));

registerWorkspaceTransientReset('activities-ui', () => useActivityUiStore.getState().reset());
